import React, { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { SendContactToCRM } from "components/integrations/SendContactToCRMButton";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { OpportunityDetails } from "lib/hooks/api/opportunities/useOpportunity";
import { useSubscription } from "lib/hooks/subscription";
import { sysPrimaryDefault } from "../../../lib/themes/colors";
import { OpportunityEntityType } from "../../../lib/types/graphQLEnums";
import { OutreachBuilderModal } from "../../outreach_builder/OutreachBuilderModal";
import Button from "../../ui/button/Button";
import { opportunitySavedCardContainer } from "./shared/styles";
import EntityCardContent from "./EntityCardContent";
import Property from "./EntityProperty";
import FindEntity from "./FindEntity";

type Contact = OpportunityDetails["contacts"][0];

type Props = {
  contacts: Contact[];
  entityUrl: string;
  opportunityId: string;
  showAddEntity?: boolean;
};

type ContactCardProps = {
  contact: Contact;
  onOutreachClick: (c: Contact) => void;
  opportunityId: string;
};

export function OpportunityContacts({ contacts, entityUrl, opportunityId, showAddEntity }: Props) {
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  return (
    <Flex column gap={16}>
      {contacts.map((contact) => (
        <ContactCard
          key={contact.id}
          contact={contact}
          onOutreachClick={(c) => setSelectedContact(c)}
          opportunityId={opportunityId}
        />
      ))}
      <FindEntity
        entity="contact"
        url={entityUrl}
        isEmptyState={!contacts.length}
        showAddEntity={showAddEntity}
      />
      <OutreachBuilderModal
        isOpen={!!selectedContact}
        onClose={() => setSelectedContact(null)}
        buyerName={selectedContact?.buyer?.name ?? ""}
        contactEmail={selectedContact?.email ?? ""}
        contactName={`${selectedContact?.firstName} ${selectedContact?.lastName}`}
        jobTitle={selectedContact?.jobTitle ?? ""}
      />
    </Flex>
  );
}

function ContactCard({ contact, onOutreachClick, opportunityId }: ContactCardProps) {
  const { data: providerTypeResponse } = useFindIntegrationProvider();
  const providerType = providerTypeResponse?.providerType;

  const subscription = useSubscription();
  const hasAccess = subscription.hasDataTypes("CONTACTS");

  const sendToCrmDropdownItem = (
    <SendContactToCRM
      key="send"
      contact={contact}
      buttonType="tertiary"
      fullWidth={true}
      justifyContent="left"
      showingTour={false}
    />
  );

  return (
    <Container aria-label="Contact card">
      <EntityCardContent
        entity="contact"
        entityId={contact.id}
        entityType={OpportunityEntityType.Contact}
        entityName={`${contact.firstName} ${contact.lastName}`}
        opportunityId={opportunityId}
        customDropdownItems={providerType ? [sendToCrmDropdownItem] : []}
      >
        <Flex gap={16} width="100%">
          <Flex flexDirection="column" gap={16} justifyContent="space-between" width="100%">
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight="600">{`${contact.firstName} ${contact.lastName}`}</Text>
            </Flex>
            <Flex column gap={16}>
              <Property title="Role" value={contact.jobTitle} icon="user01" />
              <Property title="Email" value={getEmailValue(contact)} icon="mail01" />
              <Property title="Phone" value={getPhoneValue(contact)} icon="phone" />
            </Flex>
            {hasAccess && (
              <Button
                buttonType="secondary"
                icon="messageTextCircle02"
                iconProps={{ color: sysPrimaryDefault }}
                onClick={() => onOutreachClick(contact)}
                fullWidth
              >
                Generate outreach
              </Button>
            )}
          </Flex>
        </Flex>
      </EntityCardContent>
    </Container>
  );
}

function getEmailValue(contact: Contact) {
  if (!contact.email) {
    return null;
  }

  return <EmailLink href={`mailto:${contact.email}`}>{contact.email}</EmailLink>;
}

function getPhoneValue(contact: Contact) {
  if (!contact.phone) {
    return null;
  }

  return <PhoneNumber>{contact.phone}</PhoneNumber>;
}

const EmailLink = styled.a(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
}));

const PhoneNumber = styled.span(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
}));

const Container = styled.div(({ theme }) => ({
  ...opportunitySavedCardContainer(theme),
  cursor: "default",
  flexDirection: "column",
  alignSelf: "stretch",
  gap: "16px",
  padding: "24px",
  pointer: undefined,
  "&:hover": undefined,
}));
